import React from "react";
import { X, Upload } from "lucide-react";

const ProductDetailsPopup = ({
  showProductDetails,
  setShowProductDetails,
  currentProductForDetails,
  currentStyleType,
  productDetails,
  setProductDetails,
  allProducts,
  Segment,
  selectedCategory,
  selectedSubcategory,
  productSpecs,
  categoryLimits,
  getCategoryLimits,
  getAvailableSpecifications,
  calculateTotalWastage,
  purityLimits,
  handleSaveProductDetails,
  validationErrors,
  setValidationErrors,
  handleImageUpload,
  productData,
}) => {
  if (!showProductDetails) return null;

  const currentProduct = allProducts.find(
    (p) => p.id === currentProductForDetails,
  );
  const calculatedCategoryLimits = getCategoryLimits(
    Segment,
    selectedCategory,
    selectedSubcategory,
    currentProduct?.name,
    currentStyleType,
    productDetails.specification,
  );

  const availableSpecifications = getAvailableSpecifications(
    currentProduct?.name,
    currentStyleType,
  );

  const segmentPurityLimits = purityLimits[Segment] || {};
  const categoryPurityLimits = segmentPurityLimits[selectedCategory] || {
    min: 0,
    max: 100,
  };
  const totalWastage = calculateTotalWastage(productDetails);

  const getImage = () => {
    if (
      productData &&
      productData[currentProductForDetails] &&
      productData[currentProductForDetails][currentStyleType]
    ) {
      return productData[currentProductForDetails][currentStyleType].image;
    }
    return null;
  };

  const handlePurityChange = (e) => {
    setProductDetails({ ...productDetails, purity: e.target.value });
    setValidationErrors({});
  };

  const handleWastageChange = (e) => {
    setProductDetails({ ...productDetails, wastage: e.target.value });
    setValidationErrors({});
  };

  const handleSetMCChange = (e) => {
    setProductDetails({ ...productDetails, setMC: e.target.value || 0 });
    setValidationErrors({});
  };

  const handleNetGramMCChange = (e) => {
    setProductDetails({ ...productDetails, netGramMC: e.target.value || 0 });
    setValidationErrors({});
  };

  const handleSpecificationChange = (e) => {
    const newSpec = e.target.value;
    setProductDetails((prev) => ({
      ...prev,
      specification: newSpec,
      ...(newSpec === "PLANE"
        ? {
            specificationMC: "",
            specificationGramRate: "",
          }
        : {}),
    }));
    setValidationErrors({});
  };

  const handleSpecificationMCChange = (e) => {
    setProductDetails({ ...productDetails, specificationMC: e.target.value });
    setValidationErrors({});
  };

  const handleSpecificationGramRateChange = (e) => {
    setProductDetails({
      ...productDetails,
      specificationGramRate: e.target.value,
    });
    setValidationErrors({});
  };

  return (
    <div className="product-details-overlay">
      <div className="product-details-popup">
        <div className="popup-header">
          <h3 className="popup-title">
            Enter Product Details - {currentProduct?.name} ({currentStyleType})
          </h3>
          <X
            onClick={() => setShowProductDetails(false)}
            className="popup-close-icon"
          />
        </div>
        <div className="popup-content">
          <div
            className="form-group"
            style={{ textAlign: "center", marginBottom: "25px" }}
          >
            <label className="form-label" style={{ marginBottom: "10px" }}>
              Sample Image for {currentStyleType} (1:1 Square)
            </label>
            <div className="product-image-upload-area popup-upload">
              {getImage() ? (
                <img
                  src={getImage()}
                  alt="Product Sample"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src =
                      "https://placehold.co/100x100/E0E0E0/333333?text=Error";
                  }}
                />
              ) : (
                <Upload color="white" size={48} />
              )}
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleImageUpload(e, currentProductForDetails)}
                onClick={(e) => e.stopPropagation()}
                style={{
                  position: "absolute",
                  inset: 0,
                  opacity: 0,
                  cursor: "pointer",
                }}
              />
            </div>
          </div>
          <div>
            <div className="form-group">
              <label className="form-label">Enter Purity(%) *</label>
              <input
                type="number"
                step="0.1"
                placeholder={`Min: ${categoryPurityLimits.min}, Max: ${categoryPurityLimits.max}`}
                value={productDetails.purity}
                onChange={handlePurityChange}
                className="form-input"
                required
              />
              {validationErrors.purity && (
                <span className="error-message">{validationErrors.purity}</span>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">Wastage (%) *</label>
              <input
                type="number"
                step="0.1"
                placeholder={`Max: ${
                  calculatedCategoryLimits?.maxWastageSeller || "N/A"
                }%`}
                value={productDetails.wastage}
                onChange={handleWastageChange}
                className="form-input"
                required
              />
              {validationErrors.totalWastage && (
                <span className="error-message">
                  {validationErrors.totalWastage}
                </span>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">Set MC *</label>
              <input
                type="number"
                step="0.1"
                placeholder={`Max: ${
                  calculatedCategoryLimits?.maxSetMakingSeller || "N/A"
                }`}
                value={productDetails.setMC}
                onChange={handleSetMCChange}
                className="form-input"
                required
              />
              {validationErrors.setMC && (
                <span className="error-message">{validationErrors.setMC}</span>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">Net Gram MC *</label>
              <input
                type="number"
                step="0.1"
                placeholder={`Max: ${
                  calculatedCategoryLimits?.maxGramMakingSeller || "N/A"
                }`}
                value={productDetails.netGramMC}
                onChange={handleNetGramMCChange}
                className="form-input"
                required
              />
              {validationErrors.netGramMC && (
                <span className="error-message">
                  {validationErrors.netGramMC}
                </span>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">
                {Segment === "DIAMOND" ? "Diamond Color" : "Specification"} *
              </label>
              <select
                value={productDetails.specification}
                onChange={handleSpecificationChange}
                className="form-input"
                required
              >
                <option value="">
                  Select{" "}
                  {Segment === "DIAMOND" ? "Diamond Color" : "Specification"}
                </option>
                {availableSpecifications.map((spec) => (
                  <option key={spec} value={spec}>
                    {spec}
                  </option>
                ))}
              </select>
              {validationErrors.specification && (
                <span className="error-message">
                  {validationErrors.specification}
                </span>
              )}
            </div>

            {productDetails.specification !== "PLANE" ? (
              <>
                <div className="form-group">
                  <label className="form-label">Specification wastage</label>
                  <input
                    type="number"
                    step="0.1"
                    placeholder={`Max: ${
                      calculatedCategoryLimits?.maxSpecificationMakingSeller ||
                      "N/A"
                    }`}
                    value={productDetails.specificationMC}
                    onChange={handleSpecificationMCChange}
                    className="form-input"
                  />
                  {validationErrors.specificationMC && (
                    <span className="error-message">
                      {validationErrors.specificationMC}
                    </span>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">
                    Specification{" "}
                    {Segment === "DIAMOND" ? `Carat Rate` : `Gram Rate`}
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    placeholder={`Max: ${
                      calculatedCategoryLimits?.maxSpecificationGramRateSeller ||
                      "N/A"
                    }`}
                    value={productDetails.specificationGramRate}
                    onChange={handleSpecificationGramRateChange}
                    className="form-input"
                  />
                  {validationErrors.specificationGramRate && (
                    <span className="error-message">
                      {validationErrors.specificationGramRate}
                    </span>
                  )}
                </div>
              </>
            ) : (
              ""
            )}
          </div>

          <div className="totals-section">
            <div className="total-row">
              <span className="total-label">Total Wastage:</span>
              <span className="total-value">{totalWastage.toFixed(2)}%</span>
            </div>
            <div className="total-row">
              <span className="total-label">Set Making Charges:</span>
              <span className="total-value">{productDetails.setMC || 0}</span>
            </div>
            <div className="total-row">
              <span className="total-label">Gram Making Charges:</span>
              <span className="total-value">
                {productDetails.netGramMC || 0}
              </span>
            </div>
            {productDetails.specification !== "PLANE" && (
              <div className="total-row">
                <span className="total-label">
                  {productDetails.specification}{" "}
                  {Segment === "DIAMOND" ? `Carat rate` : `Gram rate`}:
                </span>
                <span className="total-value">
                  {productDetails.specificationGramRate}
                </span>
              </div>
            )}
            {validationErrors.totalWastage && (
              <div className="error-message total-error">
                {validationErrors.totalWastage}
              </div>
            )}
            {validationErrors.totalMaking && (
              <div className="error-message total-error">
                {validationErrors.totalMaking}
              </div>
            )}
          </div>
        </div>
        <div className="popup-actions">
          <button
            onClick={() => setShowProductDetails(false)}
            className="popup-cancel-button"
          >
            Cancel
          </button>
          <button
            onClick={handleSaveProductDetails}
            className="popup-confirm-button"
            disabled={Object.keys(validationErrors).length > 0}
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductDetailsPopup;
